import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { trackClick, resolveImg } from "../../lib/api";
import { resolveFollowersDisplay, formatCount } from "../../lib/followers";

function useCountUp(target, duration = 1500, enabled = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!enabled || !target) {
      setVal(target || 0);
      return undefined;
    }
    let start;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled]);
  return val;
}

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function ProfileHeader({ settings }) {
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 150, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 150, damping: 15 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  const share = async () => {
    const url = window.location.href;
    trackClick({ item_type: "share", item_name: "Share Page" });
    if (navigator.share) {
      try {
        await navigator.share({ title: "Aly Hajiani — ThatCreditCardGuy", url });
        return;
      } catch (e) {
        /* cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const followersInfo = resolveFollowersDisplay(settings);
  const animatedTotal = useCountUp(followersInfo.numeric, 1500, followersInfo.animatable);

  return (
    <header className="relative pt-8 text-center">
      <button
        onClick={share}
        data-testid="share-button"
        aria-label="Share this page"
        className="absolute right-0 top-8 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur transition-transform active:scale-90"
      >
        {copied ? <Check size={18} className="text-[var(--accent)]" /> : <Share2 size={18} />}
      </button>

      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ perspective: 600 }}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative mx-auto w-fit"
      >
        <motion.img
          src={resolveImg(settings.avatar_url)}
          alt={settings.name}
          style={{ rotateX: rx, rotateY: ry }}
          className="h-28 w-28 rounded-full object-cover ring-2 ring-white/15 shadow-[0_16px_40px_rgba(0,0,0,0.4)]"
          data-testid="profile-avatar"
        />
      </motion.div>

      <div className="mt-6 overflow-hidden">
        <motion.h1
          variants={line}
          custom={0}
          initial="hidden"
          animate="show"
          className="text-5xl font-extrabold leading-tight tracking-tight text-white"
        >
          {settings.name}
        </motion.h1>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45, duration: 0.8 }}
        className="mx-auto mt-4 max-w-sm text-[15px] font-medium leading-relaxed text-[var(--ink-soft)]"
      >
        {settings.bio}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.7 }}
        className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-white/45"
        data-testid="aggregated-followers"
      >
        <span className="font-bold text-[var(--accent)]">
          {followersInfo.animatable ? formatCount(animatedTotal) : followersInfo.value}
        </span>{" "}· {followersInfo.label}
      </motion.p>
    </header>
  );
}
